/**
 * The `[slot]` convention shared by every composer that previews a starter
 * phrase as its placeholder.
 *
 * A starter's copy carries the slot as data — "Does this patient qualify for
 * [programme or funding]?" — and each composer decides how to draw it. Drawn
 * literally the brackets read as punctuation the clinician is meant to type;
 * drawn as a badge they read as a shape to fill in.
 *
 * Lives here rather than in one of its callers because those callers cannot
 * import each other: the Evidence chat composer is inside the Chat quarantine
 * and the Work composer is a product domain, so the two kept matching copies of
 * these classes. `src/components/ui` is neither, so it is the one place all
 * three composers can reach.
 */

import type { ReactNode } from 'react';

/** Splits on bracket-delimited slots, keeping the delimiters as their own parts. */
const SLOT_PATTERN = /(\[[^\]]+\])/g;

const isSlot = (part: string): boolean => part.startsWith('[') && part.endsWith(']');

/** Whether the text carries at least one `[slot]` that needs badge treatment. */
export const hasPlaceholderSlots = (text: string): boolean =>
  text.split(SLOT_PATTERN).some(isSlot);

/**
 * Renders `text` with its `[slot]` segments as subtle badges and everything
 * else as plain text. Returns the string untouched when there are no slots, so
 * callers can hand it any placeholder without checking first.
 */
export const renderPlaceholderSlots = (text: string): ReactNode => {
  const parts = text.split(SLOT_PATTERN);
  if (parts.length === 1) return text;

  return parts.map((part, index) =>
    isSlot(part) ? (
      <span
        key={index}
        // Shares the sentence baseline. A middle-aligned block lifts the slot off the line.
        className="inline rounded-sm bg-background-tertiary px-1.5 py-px text-text-tertiary"
      >
        {part.slice(1, -1)}
      </span>
    ) : (
      part
    )
  );
};
